import React, { FC, memo, useCallback, useEffect, useRef, useState } from 'react';

import { SortableContainer, SortableElement, SortableHandle, SortEnd, SortStart } from 'react-sortable-hoc';
import { WrappedFieldProps } from 'redux-form';
import styled from 'styled-components';

import { TFile } from '@brg/api';
import { Colors, CommonInput, CommonLabel, FlexBox } from '@brg/ui';

import { ReactComponent as SpinnerIcon } from '../Spinner.svg';
import { DeleteIcon } from '../DeleteIcon';
import { MoveIcon } from '../MoveIcon';
import { GridRow } from '../styles';
import { ProjectAddButton } from '../../../projectEdit/ProjectAddButton';
import { LabelRedStar } from '../../../designerEditView/styles';
import { FormLabel } from '../../../../modals/styles';
import { useUppyLoader, UploadedImage } from '../../../../hooks/useUppyLoader';
import useUploadValidator from '../../../../hooks/useUploadValidator';
import { LayoutConst } from 'core';

const SortableImageWrapper = styled.div`
  height: 140px;
  margin: 0 8px 8px 0;
  flex-shrink: 0;
  position: relative;

  img {
    user-select: none;
  }
`

const NegativeMarginContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin: 0 -8px -8px 0;
  position: relative;
`

const ListWrapper = styled.div`
  margin-bottom: 8px;
  position: relative;
`

const ButtonWrapper = styled.div`
  width: 100%;
  position: relative;

  input {
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    z-index: 1;
    opacity: 0;
    cursor: pointer;
  }
`

const ControlButtonsWrapper = styled(FlexBox)`
  width: 100%;
  position: absolute;
  top: 0;
  left: 0;
  justify-content: space-between;
  padding: 4px;
`

const AddButton = styled(ProjectAddButton)`
  height: 48px;
  width: 100%;
  font-weight: 600;

  @media(max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
    height: 40px;
  }

`

const Content = styled.div`
  width: 100%;
  min-height: 48px;
`

const LoaderWrapper = styled.div`
  height: 140px;
  width: 140px;
  border: 1px solid ${Colors.galBlack10};
  margin: 0 8px 8px 0;
  display: flex;
  align-items: center;
  justify-content: center;
`


const Spinner = styled(SpinnerIcon)`
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`

const Loader = () => {
  return (
    <LoaderWrapper>
        <Spinner>
        </Spinner>
    </LoaderWrapper>
  )
}

const DragHandle = SortableHandle(() => (
  <span tabIndex={0}>
    <MoveIcon size="small"/>
  </span>
));

const SortableItem = SortableElement(({ value, onRemove }: any) => (
  <SortableImageWrapper>
    <ControlButtonsWrapper>
      <DragHandle/>
      <DeleteIcon size="small" onClick={(e) => {
        onRemove(value.id, value.name)
      }}
      />
    </ControlButtonsWrapper>
    <img src={value.src} style={{ width: '100%', height: '100%', objectFit: 'cover' }} draggable="false"/>
  </SortableImageWrapper>
));

const SortableList = SortableContainer(({ items, onRemove }: any) => {
  return (
    <ListWrapper className="sortable-container">
      <NegativeMarginContainer>
        {items.map((value: any, index: number) => (
          <SortableItem key={value.id} index={index} value={value} onRemove={onRemove}/>
        ))}
      </NegativeMarginContainer>
    </ListWrapper>
  );
});

interface ImageArrayUploadFieldProps extends WrappedFieldProps {
  label: string;
  required?: boolean;
  onlyJpgFormat?: boolean;
}

const ImageArrayUploadField: FC<ImageArrayUploadFieldProps> = ({
  input: { onChange, value: inputValue },
  meta: { touched, error },
  label,
  required,
  onlyJpgFormat,
  ...rest
}) => {
  const [sortedData, setSortedData] = useState<Pick<UploadedImage, 'id' | 'name' | 'src'>[]>([]);
  const [time, setTime] = useState(Date.now())
  const [files, setFiles] = useState([])
  const validator = useUploadValidator(onlyJpgFormat);

  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    onChange(sortedData.map(({ id }) => id));
  }, [sortedData])

  const onUploadComplete = useCallback((uploadComplete: any) => {
    if (uploadComplete) {
      setFiles([])
    }
  }, [])

  const onAddImage = useCallback(async (e: any) => {
    const newFiles = Array.from(e.target.files) as TFile[];

    // @ts-ignore
    if (fileRef?.current) fileRef.current.value = ''

    setTime(Date.now());

    let multipleFilesCheckResult: any = []
    if (newFiles?.length > 0) {
      multipleFilesCheckResult = await Promise.all(
        newFiles.map(async (img: TFile) => {
          return await validator([img])
        })
      )
    }
    const allowed = await validator(files)

    if (!allowed || multipleFilesCheckResult?.includes(false)) {
      e.target.value = ''
      return
    }

    setTimeout(() => {
      // @ts-ignore
      setFiles(prev => [...(prev || []), ...newFiles]);
    }, 0)
  }, [files, fileRef])


  const [uppy, data] = useUppyLoader(files, fileRef, onUploadComplete, time);

  const onRemoveImage = useCallback((id: string, name: string) => {
    if (fileRef?.current) {
      fileRef.current.value = ''
      uppy.removeFile(id);
    }
    setTime(Date.now())
    setTimeout(() => {
      setFiles(files?.filter((file: any) => file?.name !== name))
    }, 0)
  }, [files, fileRef, uppy])

  useEffect(() => {
    setSortedData(data)
  }, [data])

  const onSortEnd = useCallback(({ oldIndex, newIndex }: SortEnd) => {
    setSortedData((prevData) => {
      const updatedData = Array.from(prevData);
      const [movedItem] = updatedData.splice(oldIndex, 1);
      updatedData.splice(newIndex, 0, movedItem);
      return updatedData;
    });
  }, []);

  const getHelperDimensions = useCallback(({ node }: SortStart) => {
    return ({
      width: (node as HTMLElement).offsetWidth,
      height: (node as HTMLElement).offsetHeight,
    })
  }, []);


  return (
    <GridRow 
      // align="center" 
      // style={{ placeItems: 'center' }} 
      isTeamEdit 
      fullWidth 
      mobileColumn
    >
      {/* {label && (
        <FormLabel isTeamEdit>
          {label}
          {required && (
            <LabelRedStar>*</LabelRedStar>
          )}
        </FormLabel>
      )} */}
      <CommonLabel 
        label={label}
        required={required}
      />
      <Content className="content-container">
        {!sortedData?.length && !!files?.length && (
          <ListWrapper>
            <NegativeMarginContainer>
              {files.map((file) => <Loader/>)}
            </NegativeMarginContainer>
          </ListWrapper>
        )}
        {!!sortedData?.length && (
          <SortableList items={sortedData}
                        onSortEnd={onSortEnd}
                        onRemove={onRemoveImage}
                        useWindowAsScrollContainer
                        helperContainer={() => document.querySelector('.sortable-container') as HTMLElement}
                        axis="xy"
                        getHelperDimensions={getHelperDimensions}
                        useDragHandle
          />
        )}
        <ButtonWrapper>
          <AddButton 
            text="Добавить"
            isProjectEdit
            reverse
          />
          {onlyJpgFormat && (
            <span className={'image-format'}> (необходимо загрузить 2 фото,файл "JPG" (.jpg) не более 5 МБ 1 фото )</span>
          )}
          <input
            type='file'
            accept='image/jpeg, image/gif, image/png'
            multiple={true}
            /*@ts-ignore*/
            ref={fileRef}
            onChange={(e) => {
              onAddImage(e)
            }}
          />
        </ButtonWrapper>
      </Content>
    </GridRow>
  );
}

export default memo(ImageArrayUploadField);
import React, { FC, memo, useCallback, useEffect, useRef, useState } from 'react';

import { SortableContainer, SortableElement, SortableHandle, SortEnd, SortStart } from 'react-sortable-hoc';
import { WrappedFieldProps } from 'redux-form';
import styled from 'styled-components';

import { TFile } from '@brg/api';
import { Colors, CommonInput, CommonLabel, FlexBox } from '@brg/ui';

import { ReactComponent as SpinnerIcon } from '../Spinner.svg';
import { DeleteIcon } from '../DeleteIcon';
import { MoveIcon } from '../MoveIcon';
import { GridRow } from '../styles';
import { ProjectAddButton } from '../../../projectEdit/ProjectAddButton';
import { LabelRedStar } from '../../../designerEditView/styles';
import { FormLabel } from '../../../../modals/styles';
import { useUppyLoader, UploadedImage } from '../../../../hooks/useUppyLoader';
import useUploadValidator from '../../../../hooks/useUploadValidator';
import { LayoutConst } from 'core';

const SortableImageWrapper = styled.div`
  height: 140px;
  margin: 0 8px 8px 0;
  flex-shrink: 0;
  position: relative;

  img {
    user-select: none;
  }
`

const NegativeMarginContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin: 0 -8px -8px 0;
  position: relative;
`

const ListWrapper = styled.div`
  margin-bottom: 8px;
  position: relative;
`

const ButtonWrapper = styled.div`
  width: 100%;
  position: relative;

  input {
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    z-index: 1;
    opacity: 0;
    cursor: pointer;
  }
`

const ControlButtonsWrapper = styled(FlexBox)`
  width: 100%;
  position: absolute;
  top: 0;
  left: 0;
  justify-content: space-between;
  padding: 4px;
`

const AddButton = styled(ProjectAddButton)`
  height: 48px;
  width: 100%;
  font-weight: 600;

  @media(max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
    height: 40px;
  }

`

const Content = styled.div`
  width: 100%;
  min-height: 48px;
`

const LoaderWrapper = styled.div`
  height: 140px;
  width: 140px;
  border: 1px solid ${Colors.galBlack10};
  margin: 0 8px 8px 0;
  display: flex;
  align-items: center;
  justify-content: center;
`


const Spinner = styled(SpinnerIcon)`
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`

const Loader = () => {
  return (
    <LoaderWrapper>
        <Spinner>
        </Spinner>
    </LoaderWrapper>
  )
}

const DragHandle = SortableHandle(() => (
  <span tabIndex={0}>
    <MoveIcon size="small"/>
  </span>
));

const SortableItem = SortableElement(({ value, onRemove }: any) => (
  <SortableImageWrapper>
    <ControlButtonsWrapper>
      <DragHandle/>
      <DeleteIcon size="small" onClick={(e) => {
        onRemove(value.id, value.name)
      }}
      />
    </ControlButtonsWrapper>
    <img src={value.src} style={{ width: '100%', height: '100%', objectFit: 'cover' }} draggable="false"/>
  </SortableImageWrapper>
));

const SortableList = SortableContainer(({ items, onRemove }: any) => {
  return (
    <ListWrapper className="sortable-container">
      <NegativeMarginContainer>
        {items.map((value: any, index: number) => (
          <SortableItem key={value.id} index={index} value={value} onRemove={onRemove}/>
        ))}
      </NegativeMarginContainer>
    </ListWrapper>
  );
});

interface ImageArrayUploadFieldProps extends WrappedFieldProps {
  label: string;
  required?: boolean;
  onlyJpgFormat?: boolean;
}

const ImageArrayUploadField: FC<ImageArrayUploadFieldProps> = ({
  input: { onChange, value: inputValue },
  meta: { touched, error },
  label,
  required,
  onlyJpgFormat,
  ...rest
}) => {
  const [sortedData, setSortedData] = useState<Pick<UploadedImage, 'id' | 'name' | 'src'>[]>([]);
  const [time, setTime] = useState(Date.now())
  const [files, setFiles] = useState([])
  const validator = useUploadValidator(onlyJpgFormat);

  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    onChange(sortedData.map(({ id }) => id));
  }, [sortedData])

  const onUploadComplete = useCallback((uploadComplete: any) => {
    if (uploadComplete) {
      setFiles([])
    }
  }, [])

  const onAddImage = useCallback(async (e: any) => {
    const newFiles = Array.from(e.target.files) as TFile[];

    // @ts-ignore
    if (fileRef?.current) fileRef.current.value = ''

    setTime(Date.now());

    let multipleFilesCheckResult: any = []
    if (newFiles?.length > 0) {
      multipleFilesCheckResult = await Promise.all(
        newFiles.map(async (img: TFile) => {
          return await validator([img])
        })
      )
    }
    const allowed = await validator(files)

    if (!allowed || multipleFilesCheckResult?.includes(false)) {
      e.target.value = ''
      return
    }

    setTimeout(() => {
      // @ts-ignore
      setFiles(prev => [...(prev || []), ...newFiles]);
    }, 0)
  }, [files, fileRef])


  const [uppy, data] = useUppyLoader(files, fileRef, onUploadComplete, time);

  const onRemoveImage = useCallback((id: string, name: string) => {
    if (fileRef?.current) {
      fileRef.current.value = ''
      uppy.removeFile(id);
    }
    setTime(Date.now())
    setTimeout(() => {
      setFiles(files?.filter((file: any) => file?.name !== name))
    }, 0)
  }, [files, fileRef, uppy])

  useEffect(() => {
    setSortedData(data)
  }, [data])

  const onSortEnd = useCallback(({ oldIndex, newIndex }: SortEnd) => {
    setSortedData((prevData) => {
      const updatedData = Array.from(prevData);
      const [movedItem] = updatedData.splice(oldIndex, 1);
      updatedData.splice(newIndex, 0, movedItem);
      return updatedData;
    });
  }, []);

  const getHelperDimensions = useCallback(({ node }: SortStart) => {
    return ({
      width: (node as HTMLElement).offsetWidth,
      height: (node as HTMLElement).offsetHeight,
    })
  }, []);


  return (
    <GridRow 
      // align="center" 
      // style={{ placeItems: 'center' }} 
      isTeamEdit 
      fullWidth 
      mobileColumn
    >
      {/* {label && (
        <FormLabel isTeamEdit>
          {label}
          {required && (
            <LabelRedStar>*</LabelRedStar>
          )}
        </FormLabel>
      )} */}
      <CommonLabel 
        label={label}
        required={required}
      />
      <Content className="content-container">
        {!sortedData?.length && !!files?.length && (
          <ListWrapper>
            <NegativeMarginContainer>
              {files.map((file) => <Loader/>)}
            </NegativeMarginContainer>
          </ListWrapper>
        )}
        {!!sortedData?.length && (
          <SortableList items={sortedData}
                        onSortEnd={onSortEnd}
                        onRemove={onRemoveImage}
                        useWindowAsScrollContainer
                        helperContainer={() => document.querySelector('.sortable-container') as HTMLElement}
                        axis="xy"
                        getHelperDimensions={getHelperDimensions}
                        useDragHandle
          />
        )}
        <ButtonWrapper>
          <AddButton 
            text="Добавить"
            isProjectEdit
            reverse
          />
          {onlyJpgFormat && (
            <span className={'image-format'}> (необходимо загрузить 2 фото,файл "JPG" (.jpg) не более 5 МБ 1 фото )</span>
          )}
          <input
            type='file'
            accept='image/jpeg, image/gif, image/png'
            multiple={true}
            /*@ts-ignore*/
            ref={fileRef}
            onChange={(e) => {
              onAddImage(e)
            }}
          />
        </ButtonWrapper>
      </Content>
    </GridRow>
  );
}

export default memo(ImageArrayUploadField);
